import { FC } from 'react'
import { IUserModel } from '../../../../store/models/User.model'
import { AccountIcon } from '../../../../../shared/components/icons/nav/AccountIcon'
import { AdvertisingResearchIcon } from '../../../../../shared/components/icons/nav/AdvertisingResearchIcon'
import { AffiliateComplianceIcon } from '../../../../../shared/components/icons/nav/AffiliateComplianceIcon'
import { BlackhatMonitoringIcon } from '../../../../../shared/components/icons/nav/BlackhatMonitoringIcon'
import { BrandMentionsIcon } from '../../../../../shared/components/icons/nav/BrandMentionsIcon'
import { BrandProtectionIcon } from '../../../../../shared/components/icons/nav/BrandProtectionIcon'
import { EarnIcon } from '../../../../../shared/components/icons/nav/EarnIcon'
import { HelpIcon } from '../../../../../shared/components/icons/nav/HelpIcon'
import { HelpMenu } from './ui/HelpMenu'
import { MoreToolsIcon } from '../../../../../shared/components/icons/nav/MoreToolsIcon'
import { NonCompliantContentIcon } from '../../../../../shared/components/icons/nav/NonCompliantContentIcon'
import { OrganicResultsIcon } from '../../../../../shared/components/icons/nav/OrganicResultsIcon'
import { PaidResultsIcon } from '../../../../../shared/components/icons/nav/PaidResultsIcon'
import { LogoutIcon } from '../../../../../shared/components/icons/nav/LogoutIcon'
import { PositionTrackerIcon } from '../../../../../shared/components/icons/nav/PositionTrackerIcon'
import { ProfileIcon } from '../../../../../shared/components/icons/nav/ProfileIcon'
import { ProjectIcon } from '../../../../../shared/components/icons/nav/ProjectIcon'
import { SERPIcon } from '../../../../../shared/components/icons/nav/SERPIcon'
import { TeamIcon } from '../../../../../shared/components/icons/nav/TeamIcon'
import { TrademarkInfringementsIcon } from '../../../../../shared/components/icons/nav/TrademarkInfringementsIcon'
import { UncloakingIcon } from '../../../../../shared/components/icons/nav/UncloakingIcon'
import { HeaderIcon } from '../../../../../shared/components/icons/nav/HeaderIcon'

/** Пункт виден не всем: условие считаем по текущему пользователю */
export interface INavVisibility {
  isVisible?: (user: IUserModel) => boolean
}

export interface INavLink extends INavVisibility {
  /** Ключ перевода: подпись собираем через t() уже при отрисовке */
  label: string
  to: string
  icon?: FC
}

/** Пункт без маршрута: обработчик отдаёт лэйаут по id — например, logout */
export interface INavAction extends INavVisibility {
  id: string
  /** Ключ перевода */
  label: string
  icon?: FC
}

export interface INavSection {
  /** Ключ перевода заголовка раздела */
  title?: string
  items: (INavLink | INavAction)[]
}

export interface INavMenuContentProps {
  /** Пункты на светлой подложке — в поповере свёрнутого меню */
  light?: boolean
  /** Закрыть поповер после выбора пункта */
  onSelect?: () => void
}

export interface INavGroup extends INavVisibility {
  id: string
  /** Ключ перевода */
  label: string
  icon: FC
  sections?: INavSection[]
  /** Своё содержимое вместо списка ссылок — например, меню помощи */
  menu?: FC<INavMenuContentProps>
}

export type INavItem = INavLink | INavAction | INavGroup

export const isNavGroup = (item: INavItem): item is INavGroup =>
  'sections' in item || 'menu' in item

export const isNavLink = (item: INavLink | INavAction): item is INavLink => 'to' in item

export const MAIN_NAVIGATION: (INavLink | INavGroup)[] = [
  {
    label: 'navigation.projects',
    to: '/projects',
    icon: ProjectIcon,
    isVisible: (user) => ['editor', 'admin'].includes(user.sub_role),
  },
  {
    id: 'serp-monitoring',
    label: 'navigation.SERPMonitoring',
    icon: SERPIcon,
    sections: [
      {
        items: [
          { label: 'navigation.paidSearch', to: '/ad-reports', icon: PaidResultsIcon },
          { label: 'navigation.organicSearch', to: '/organic-results', icon: OrganicResultsIcon },
        ],
      },
    ],
  },
  {
    id: 'brand-protection',
    label: 'navigation.brandProtection',
    icon: BrandProtectionIcon,
    sections: [
      {
        items: [
          { label: 'navigation.affiliateCompliance', to: '/ppc-compliance', icon: AffiliateComplianceIcon },
          {
            label: 'navigation.trademarkInfringements',
            to: '/tm-infringement',
            icon: TrademarkInfringementsIcon,
            isVisible: (user) => user.tm_infringements_enabled,
          },
          {
            label: 'navigation.nonCompliantContent',
            to: '/language-compliance-page',
            icon: NonCompliantContentIcon,
          },
        ],
      },
    ],
  },
  {
    id: 'more-tools',
    label: 'navigation.moreTools',
    icon: MoreToolsIcon,
    sections: [
      {
        title: 'navigation.researchTools',
        items: [
          { label: 'navigation.SEOPositionTracker', to: '/position-tracker', icon: PositionTrackerIcon },
          { label: 'navigation.onSiteBrandMentions', to: '/serp-presence', icon: BrandMentionsIcon },
          {
            label: 'navigation.advertisingResearch',
            to: '/advertising-research',
            icon: AdvertisingResearchIcon,
          },
        ],
      },
      {
        title: 'navigation.uncloaking',
        items: [
          {
            label: 'navigation.liveUncloaking',
            to: '/live-uncloaking',
            icon: UncloakingIcon,
            isVisible: (user) => user.allow_decloak_live,
          },
          {
            label: 'navigation.blackhatMonitoring',
            to: '/blackhat-monitoring',
            icon: BlackhatMonitoringIcon,
            isVisible: (user) => user.allow_decloak_auto,
          },
        ],
      },
    ],
  },
  {
    label: 'navigation.earn',
    to: '/referral-program',
    icon: EarnIcon,
    isVisible: () => process.env.REACT_APP_PROJECT !== 'robuscan',
  },
]

export const SERVICE_NAVIGATION: INavGroup[] = [
  { id: 'help', label: 'navigation.help', icon: HelpIcon, menu: HelpMenu },
  {
    id: 'account',
    label: 'navigation.account',
    icon: AccountIcon,
    sections: [
      {
        items: [
          { label: 'pages.profile.profile', to: '/profile-settings', icon: ProfileIcon },
          {
            label: 'pages.profile.team',
            to: '/team',
            icon: TeamIcon,
            isVisible: (user) => user.sub_role === 'admin',
          },
          { id: 'switch-menu', label: 'navigation.switchToTopMenu', icon: HeaderIcon },
          { id: 'logout', label: 'pages.profile.logout', icon: LogoutIcon },
        ],
      },
    ],
  },
]

// Именно так, а не через `?? true`: условие вернуло бы undefined для отсутствующего
// доступа, и пункт остался бы виден
const isVisibleFor = (item: INavVisibility, user: IUserModel) =>
  !item.isVisible || !!item.isVisible(user)

/**
 * Меню под конкретного пользователя: скрываем пункты по их условиям, а следом пустые
 * секции и группы — например, «Uncloaking» целиком, если не открыт ни один из отчётов
 */
export const getVisibleNavigation = <T extends INavItem>(items: T[], user: IUserModel): T[] =>
  items
    .filter((item) => isVisibleFor(item, user))
    .map((item) =>
      isNavGroup(item) && item.sections
        ? {
            ...item,
            sections: item.sections
              .map((section) => ({
                ...section,
                items: section.items.filter((sectionItem) => isVisibleFor(sectionItem, user)),
              }))
              .filter((section) => section.items.length),
          }
        : item,
    )
    .filter((item) => !isNavGroup(item) || !!item.menu || !!item.sections?.length)

/** Пункт активен и на вложенных маршрутах: /projects подсвечен и на /projects/1 */
const matchesPath = (to: string, pathname: string) =>
  pathname === to || pathname.startsWith(`${to}/`)

/**
 * Ссылка верхнего уровня приоритетнее вложенной: пока часть вложенных пунктов
 * ведёт на плейсхолдер, иначе вместе с ней подсвечивалась бы чужая группа
 */
const isRootLinkActive = (pathname: string) =>
  [...MAIN_NAVIGATION, ...SERVICE_NAVIGATION].some(
    (item) => !isNavGroup(item) && isNavLink(item) && matchesPath(item.to, pathname),
  )

export const isNavLinkActive = (link: INavLink, pathname: string, nested = false) =>
  matchesPath(link.to, pathname) && !(nested && isRootLinkActive(pathname))

export const isNavGroupActive = (group: INavGroup, pathname: string) =>
  !isRootLinkActive(pathname) &&
  !!group.sections?.some((section) =>
    section.items.some((item) => isNavLink(item) && matchesPath(item.to, pathname)),
  )

/** Группа меню, которую нужно раскрыть на текущем маршруте: в каждом меню она одна */
export const getNavGroupIdByPath = (navigation: INavItem[], pathname: string): string | null =>
  navigation.filter(isNavGroup).find((group) => isNavGroupActive(group, pathname))?.id ?? null
